<script lang="ts" setup>
import { ref, computed, watch, onMounted } from 'vue';
import moment from 'moment';
import { useFilterStore, useUiStore } from '@/stores';
import { AgentDeviceStatusHistoryReportService, toastServiceError } from '@/services';
import { formatSecToTime } from '@/utils/datetime';
import {
    Permission,
    UserDeviceType,
    AgentDeviceStatusHistoryReportServiceGetInfoResult,
} from '@/types';

import SVGChromeLogo from '@/components/svg/SVGChromeLogo.vue';
import SVGDeviceExternal from '@/components/svg/SVGDeviceExternal.vue';
import { DesktopComputerIcon } from '@heroicons/vue/solid';
import { DeviceMobileIcon } from '@heroicons/vue/outline';

const filterStore = useFilterStore();
const uiStore = useUiStore();

const dateRange = computed({
    get: () => filterStore.dateRange,
    set: (range: Array<Date>) => filterStore.setDateRange(range),
});

const levelFilter = computed(() => filterStore.level);
const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const agents = ref<AgentDeviceStatusHistoryReportServiceGetInfoResult[]>([]);
const totalTimePaused = ref(0);
const totalTimeCall = ref(0);

const tableHeaders = [
    { key: 'name', label: 'report.agentDeviceStatusHistory.details.userName', align: 'left' },
    {
        key: 'extension',
        label: 'report.agentDeviceStatusHistory.details.extension',
        align: 'center',
    },
    {
        key: 'queues',
        label: 'report.agentDeviceStatusHistory.details.queues',
        align: 'center',
    },
    {
        key: 'totalConnectedTime',
        label: 'report.agentDeviceStatusHistory.details.totalTimeOnline',
        align: 'center',
    },
    {
        key: 'totalPauseTime',
        label: 'report.agentDeviceStatusHistory.details.totalTimePaused',
        align: 'center',
    },
    {
        key: 'totalCallDuration',
        label: 'report.agentDeviceStatusHistory.details.totalTimeInCall',
        align: 'center',
    },
];

const getDeviceIcon = (deviceType: string) =>
    ({
        [UserDeviceType.MOBILE]: DeviceMobileIcon,
        [UserDeviceType.DESKTOP]: DesktopComputerIcon,
        [UserDeviceType.WEB]: SVGChromeLogo,
        [UserDeviceType.EXTERNAL]: SVGDeviceExternal,
    })[deviceType];

const getReportData = async () => {
    const params = {
        startDate: moment.utc(dateRange.value[0]).format('YYYY-MM-DD'),
        endDate: moment.utc(dateRange.value[1]).format('YYYY-MM-DD'),
    };

    try {
        setLoading(true);
        const { data } = await AgentDeviceStatusHistoryReportService.getInfo({ params });

        agents.value = data;
        totalTimePaused.value = data.reduce((acc, curr) => acc + (curr.totalPauseTime || 0), 0);
        totalTimeCall.value = data.reduce((acc, curr) => acc + (curr.totalCallDuration || 0), 0);
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

watch([levelFilter, dateRange], getReportData);
onMounted(getReportData);
</script>

<template>
    <div class="space-y-4 p-4 sm:p-6 lg:p-8">
        <UiPageHeader>
            <template #info>
                <h1 class="text-xl font-semibold text-gray-800">
                    {{ $t('report.agentDeviceStatusHistory.title') }}
                </h1>
            </template>
            <template #actions>
                <UiDatePicker v-model="dateRange" range multiCalendars />
            </template>
        </UiPageHeader>

        <dl class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <UiPanel v-for="(value, key) in { totalTimePaused, totalTimeCall }" :key="key">
                <dt class="text-center text-xs font-medium text-gray-500">
                    {{ $t(`report.agentDeviceStatusHistory.${key}`) }}
                </dt>
                <dd class="mt-1 text-center text-3xl font-semibold text-gray-800">
                    {{ formatSecToTime(value) }}
                </dd>
            </UiPanel>
        </dl>

        <UiPanel no-padding>
            <table class="w-full border-collapse text-sm">
                <thead class="sticky top-0 bg-gray-200 text-gray-800">
                    <tr>
                        <th
                            v-for="header in tableHeaders"
                            :key="header.key"
                            class="p-4"
                            :class="header.align === 'center' ? 'text-center' : 'text-left'"
                        >
                            {{ $t(header.label) }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(agent, idx) in agents" :key="idx" class="even:bg-gray-100">
                        <!-- Name -->
                        <td class="align-center p-4">
                            {{ agent.name }}
                        </td>

                        <!-- Extension -->
                        <td class="align-center p-4 text-center">
                            {{ agent.extension }}
                        </td>

                        <!-- Queues (multiple lines) -->
                        <td class="p-4 text-center align-middle">
                            <div
                                v-for="queue in agent.queues"
                                :key="queue.id"
                                class="flex items-center justify-center gap-1"
                            >
                                <component
                                    :is="$can(Permission.VIEW_QUEUE) ? 'RouterLink' : 'span'"
                                    :to="{
                                        name: 'ViewQueueById',
                                        params: {
                                            id: queue?.id,
                                        },
                                    }"
                                    class="text-primary"
                                >
                                    {{ queue.name }}
                                </component>
                            </div>
                        </td>

                        <!-- Connected time (multiple lines) -->
                        <td class="p-4 text-center align-middle">
                            <div
                                v-for="device in agent.userDevices"
                                :key="device.userDeviceId"
                                class="flex items-center justify-center gap-1"
                            >
                                <component :is="getDeviceIcon(device.deviceType)" class="h-4 w-4" />
                                <span>{{ formatSecToTime(device.totalConnectedTime, true) }}</span>
                            </div>
                        </td>

                        <!-- Paused Time -->
                        <td class="align-center p-4 text-center">
                            {{ formatSecToTime(agent.totalPauseTime, true) }}
                        </td>

                        <!-- Call Duration -->
                        <td class="align-center p-4 text-center">
                            {{ formatSecToTime(agent.totalCallDuration, true) }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </UiPanel>
    </div>
</template>
